import type { PlayerInformationsSchema } from "@teamz/common";

import EditIcon from "@/assets/edit.svg";
import HeartIcon from "@/assets/heart.svg";
import MsgIcon from "@/assets/msg-none.svg";
import ShareIcon from "@/assets/share.svg";
import { useUser } from "@clerk/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { playerInformationsSchema } from "@teamz/common";
import { Lock, UserRound } from "lucide-react";
import { useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button.styles";
import { useMe } from "@/context/user-context";
import { useOpenMessageThread } from "@/hooks/use-open-message-thread";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { tDyn } from "@/lib/i18n-dynamic";
import { useTRPC } from "@/lib/trpc";
import { cn, fileToURL, uploadClerkProfileImage } from "@/lib/utils";
import { routes } from "@/routes";

import { CountryCombobox } from "../country-combobox";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { ErrorField, RootField, TextAreaField, TextField } from "../ui/text-field";

export function PlayerInformations({ player }: { player: RouterOutputs["player"]["getPlayer"]; playerId?: string }) {
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const { t } = useTranslation(["common", "profile"]);
  const { getCountryName } = useTranslationUtils();
  const openMessageThread = useOpenMessageThread();
  const { user } = useMe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const isFreemiumClub = user.role === "club" && !user.plan && !user.isAdmin;

  const { data } = useQuery(trpc.club.getIsPlayerFavorite.queryOptions(
    { playerId: player.id },
    { enabled: user.role === "club" },
  ));

  const { mutate } = useMutation(trpc.club.updateFavoritesPlayers.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getIsPlayerFavorite.queryFilter());
    },
  }));

  const updateFavorite = () => {
    mutate({
      playerId: player.id,
    });
  };

  function upsertThread() {
    openMessageThread(player.userId);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4">
          {/* Fiche servie à une structure freemium : le serveur n'a envoyé ni
              photo ni vrai nom, l'UI se contente de rendre le remplacement
              lisible comme tel. */}
          {player.isAnonymized
            ? (
                <div className="flex size-[88px] shrink-0 items-center justify-center rounded-xl bg-neutral-100 text-neutral-400">
                  <UserRound className="size-10" />
                </div>
              )
            : (
                <img
                  src={player.imgUrl}
                  alt={`Profil de ${player.firstName}`}
                  className="pointer-events-none size-[88px] select-none rounded-xl object-cover"
                />
              )}
          <div className="flex flex-col gap-1">
            {player.isAnonymized
              ? (
                  <div className="flex items-center gap-2">
                    <h1 aria-hidden className="select-none text-wrap text-2xl font-semibold text-neutral-450 blur-[6px]">
                      {player.fullName}
                    </h1>
                    <span className="sr-only">{t("common:freemium.locked-player")}</span>
                    <Lock className="size-4 shrink-0 text-neutral-400" />
                  </div>
                )
              : (
                  <h1 className="text-wrap text-2xl font-semibold">
                    {player.fullName}
                  </h1>
                )}

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex w-auto max-w-48 items-center gap-2 rounded-full bg-neutral-100 p-0.5">
                <CircleFlag countryCode={player.nationalityCode.toLowerCase()} className="size-5 rounded-full" />
                <span className="truncate pr-3 text-sm">
                  {getCountryName(player.nationalityCode)}
                </span>
              </div>

              {player.gender === "male"
                ? (
                    <Badge variant="blue" size="compact">
                      H
                    </Badge>
                  )
                : (
                    <Badge variant="purple" size="compact">
                      F
                    </Badge>
                  )}

              <span className="text-nowrap font-medium text-neutral-500">
                {t("profile:years", { age: player.age })}
              </span>

              {!isFreemiumClub && player.isNew && <Badge variant="orange">{t("common:new")}</Badge>}
            </div>
          </div>
        </div>

        {(user.role !== "club" || player.description.length > 0) && (
          <p className="whitespace-pre-line px-2 text-sm text-neutral-600">
            {player.description.length > 0 ? player.description : t("common:no-description")}
          </p>
        )}
      </div>

      <div className="flex flex-1 items-end gap-2">
        { (user.role !== "club" || user.isAdmin)
          ? (
              <>
                <Sheet open={modalState === "open"} onOpenChange={isOpen => updateModalState(isOpen ? "open" : "close")}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="w-full">
                      <EditIcon />
                      {t("common:edit")}
                    </Button>
                  </SheetTrigger>
                  <SheetContent>
                    <SheetHeader>
                      <SheetTitle>
                        {t("common:mobile-nav.profile")}
                      </SheetTitle>
                    </SheetHeader>

                    <PlayerInformationsForm player={player} closeModal={() => updateModalState("close")} />
                  </SheetContent>
                </Sheet>

                <Button variant="outline" className="w-full" disabled>
                  <ShareIcon />
                  {t("common:share")}
                </Button>
              </>
            )
          : (
              <>
                <Button
                  variant="outline"
                  onClick={updateFavorite}
                  className="w-full active:border-neutral-300"
                >
                  <HeartIcon
                    className={
                      data?.isFavorite
                        ? "fill-[#FE5000] "
                        : "fill-white "
                    }
                  />
                  {t("common:mobile-nav.favorites")}
                </Button>
                {/* Écrire à un joueur reste réservé aux abonnés : le club freemium
                    est envoyé sur la messagerie, qui lui en montre l'aperçu et porte
                    le bouton d'abonnement. */}
                {isFreemiumClub
                  ? (
                      <Link to={routes.messages} className={cn(buttonVariants({ variant: "default" }), "w-full")}>
                        <MsgIcon />
                        {t("common:freemium.unlock-contact")}
                      </Link>
                    )
                  : (
                      <Button variant="outline" className="w-full" onClick={upsertThread}>
                        <MsgIcon />
                        {t("common:msg")}
                      </Button>
                    )}
              </>
            )}
      </div>
    </div>
  );
}

function PlayerInformationsForm({ player, closeModal }: { player: RouterOutputs["player"]["getPlayer"]; closeModal: () => void }) {
  const playerInformationsForm = useForm<PlayerInformationsSchema>({
    resolver: zodResolver(playerInformationsSchema),
    // birthdate est absent d'une fiche anonymisée, mais ce formulaire n'est
    // rendu que pour le joueur sur son propre profil, jamais anonymisé.
    defaultValues: { ...player, birthdate: player.birthdate ? new Date(player.birthdate).toISOString().split("T")[0] : "" },
  });
  const [descriptionLength, setDescriptionLength] = useState(player.description.length);
  const { user } = useMe();
  const { user: clerkUser } = useUser();
  const [profileImageFile, setProfileImageFile] = useState<FileList | null>(null);
  const [clerkProfileImageError, setClerkProfileImageError] = useState<string>();
  const { t } = useTranslation(["common"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.player.updateProfile.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getPlayer.queryFilter());
      closeModal();
    },
  }));

  const today = new Date().toISOString().split("T")[0];

  const onSubmit = playerInformationsForm.handleSubmit(async (data) => {
    const profileImgFile = profileImageFile?.[0];

    // Garde en profondeur : l'upload Clerk cible toujours le compte connecté,
    // jamais celui d'un profil tiers édité par un admin.
    if (!profileImgFile || user.id !== player.userId) {
      return mutate({ playerInfos: { ...data, userId: player.userId } });
    }

    const clerkRes = await uploadClerkProfileImage(profileImgFile, clerkUser);

    if (clerkRes.status === "error") {
      setClerkProfileImageError(clerkRes.errorMsg);
      return;
    }

    mutate({ playerInfos: { ...data, userId: player.userId, imgUrl: clerkRes.publicUrl } });
  });

  const playerFormErrors = playerInformationsForm.formState.errors;

  return (
    <>
      <SheetForm id="PlayerInformationsForm" onSubmit={onSubmit}>
        {/* La photo passe par le compte Clerk connecté : un admin qui édite le profil
            d'un autre joueur écraserait son propre avatar, on masque donc le champ. */}
        {user.id === player.userId && (
          <div className="flex w-full items-center gap-2">
            <label className="relative shrink-0 cursor-pointer rounded-2xl bg-neutral-600  p-1.5">
              <img
                src={profileImageFile?.[0] ? fileToURL(profileImageFile[0]) : player.imgUrl}
                alt={`Profil de ${player.firstName}`}
                className="pointer-events-none size-[88px] select-none rounded-xl object-cover"
              />
              <div className="absolute -bottom-2 -right-2 flex size-8 items-center justify-center rounded-full bg-neutral-500 text-neutral-300">
                <EditIcon className="size-5" />
              </div>
              <input
                type="file"
                multiple={false}
                accept="image/jpeg,image/png,image/jpg"
                onChange={e => setProfileImageFile(e.target.files)}
                className="sr-only"
              />
            </label>
            <div className="flex flex-col gap-1">
              <Label>{t("common:profile-picture")}</Label>
              {clerkProfileImageError && <ErrorField>{clerkProfileImageError}</ErrorField>}
            </div>
          </div>
        )}

        <TextField
          label={t("common:firstname")}
          errorMessage={tDyn(t, playerFormErrors.firstName?.message)}
          {...playerInformationsForm.register("firstName")}
        />
        <TextField
          label={t("common:lastname")}
          errorMessage={tDyn(t, playerFormErrors.lastName?.message)}
          {...playerInformationsForm.register("lastName")}
        />
        <TextAreaField
          label={(
            <span className="inline-flex w-full justify-between child:block">
              <span>{t("common:description")}</span>
              <span className={cn("text-neutral-450", descriptionLength > 730 && "text-orange-300", descriptionLength === 750 && "text-orange-500")}>
                {descriptionLength}
                /750
              </span>
            </span>
          )}
          errorMessage={tDyn(t, playerFormErrors.description?.message)}
          {...playerInformationsForm.register("description", {
            onChange(event) {
              if (event.target.value.trim().length > 750) {
                event.target.value = event.target.value.trim().slice(0, 750);
              }
              if (event.target.value.trim().length === 0) {
                event.target.value = "";
              }
              setDescriptionLength(event.target.value.length);
            },
          })}
        />
        <TextField
          label={t("common:birthdate")}
          max={today}
          type="date"
          errorMessage={tDyn(t, playerFormErrors.birthdate?.message)}
          {...playerInformationsForm.register("birthdate")}
        />
        <Controller
          control={playerInformationsForm.control}
          name="gender"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("common:gender")}</Label>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="male">{t("common:man")}</SelectItem>
                    <SelectItem value="female">{t("common:woman")}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error?.message && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />

        <Controller
          control={playerInformationsForm.control}
          name="nationalityCode"
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("common:nationality-country")}</Label>
              <CountryCombobox field={field} />
              {fieldState.error && (
                <ErrorField>{tDyn(t, fieldState.error?.message)}</ErrorField>
              )}
            </RootField>
          )}
        />
      </SheetForm>
      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="PlayerInformationsForm" loading={isPending || playerInformationsForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
}
